import type {
  AssetKpiData,
  AssetKpiPreference,
  AssetKpiPreferenceRequest,
  CannedKpi,
  CreateKpiPreferenceRequest,
  PortfolioKpiData,
  UpdateKpiPreferenceRequest,
  UserKpiPreference,
} from '../types/kpi';
import { apiClient } from './ApiClient';

export const KpiPreferencesService = {
  // Canned KPIs
  getCannedKpis: async (
    category?: string,
    scope?: 'asset' | 'portfolio',
  ): Promise<CannedKpi[]> => {
    const params: Record<string, any> = {};
    if (category) params.category = category;
    if (scope) params.scope = scope;

    const response = await apiClient.get('/kpi-preferences/canned', { params });
    return response.data;
  },

  // Portfolio KPI calculations
  calculatePortfolioKpis: async (
    period = 'annual',
    startDate?: string,
    endDate?: string,
  ): Promise<PortfolioKpiData> => {
    const params: Record<string, any> = { period };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await apiClient.get(
      '/kpi-preferences/portfolio/calculate',
      { params },
    );
    return response.data;
  },

  // User KPI preferences
  getUserKpiPreferences: async (
    scope?: 'dashboard' | 'asset',
  ): Promise<UserKpiPreference[]> => {
    const params: Record<string, any> = {};
    if (scope) params.scope = scope;

    const response = await apiClient.get('/kpi-preferences/preferences', {
      params,
    });
    return response.data;
  },

  createUserKpiPreference: async (
    preference: CreateKpiPreferenceRequest,
  ): Promise<UserKpiPreference> => {
    const response = await apiClient.post(
      '/kpi-preferences/preferences',
      preference,
    );
    return response.data;
  },

  updateUserKpiPreference: async (
    id: number,
    updates: UpdateKpiPreferenceRequest,
  ): Promise<UserKpiPreference> => {
    const response = await apiClient.put(
      `/kpi-preferences/preferences/${id}`,
      updates,
    );
    return response.data;
  },

  deleteUserKpiPreference: async (id: number): Promise<void> => {
    await apiClient.delete(`/kpi-preferences/preferences/${id}`);
  },

  // Asset KPI preferences
  getAssetKpiPreferences: async (
    assetId: string,
  ): Promise<AssetKpiPreference[]> => {
    const response = await apiClient.get(
      `/kpi-preferences/assets/${assetId}/preferences`,
    );
    return response.data;
  },

  createAssetKpiPreference: async (
    assetId: string,
    preference: AssetKpiPreferenceRequest,
  ): Promise<AssetKpiPreference> => {
    const response = await apiClient.post(
      `/kpi-preferences/assets/${assetId}/preferences`,
      preference,
    );
    return response.data;
  },

  updateAssetKpiPreference: async (
    id: number,
    updates: UpdateKpiPreferenceRequest,
  ): Promise<AssetKpiPreference> => {
    const response = await apiClient.put(
      `/kpi-preferences/assets/preferences/${id}`,
      updates,
    );
    return response.data;
  },

  deleteAssetKpiPreference: async (
    assetId: string,
    id: number,
  ): Promise<void> => {
    await apiClient.delete(`/kpi-preferences/assets/preferences/${id}`);
  },

  // Setup default KPIs for an asset
  setupDefaultAssetKpis: async (assetId: string): Promise<any> => {
    const response = await apiClient.post(
      `/kpi-preferences/assets/${assetId}/setup-defaults`,
    );
    return response.data;
  },

  // Asset KPI calculations
  calculateAssetKpis: async (
    assetId: string,
    period = 'annual',
    startDate?: string,
    endDate?: string,
  ): Promise<AssetKpiData> => {
    const params: Record<string, any> = { period };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await apiClient.get(
      `/kpi-preferences/assets/${assetId}/calculate`,
      { params },
    );
    return response.data;
  },

  // Setup default KPI preferences for new users
  setupDefaultPreferences: async (): Promise<any> => {
    const response = await apiClient.post('/kpi-preferences/setup-defaults');
    return response.data;
  },

  // Check if user has any KPI preferences (including disabled ones)
  hasAnyKpiPreferences: async (
    scope?: 'dashboard' | 'asset',
  ): Promise<boolean> => {
    const params: Record<string, any> = {};
    if (scope) params.scope = scope;

    const response = await apiClient.get('/kpi-preferences/preferences/any', {
      params,
    });
    return response.data.hasAnyPreferences;
  },

  // Check if user has any KPI preferences
  hasPreferences: async (scope?: 'dashboard' | 'asset'): Promise<boolean> => {
    const preferences =
      await KpiPreferencesService.getUserKpiPreferences(scope);
    return preferences.length > 0;
  },
};

export default KpiPreferencesService;
